import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../hooks/useToast';
import AddressAutocomplete from '../ui/AddressAutocomplete';

const Acente_TalepOlustur = ({ user }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [transfers, setTransfers] = useState([
    {
      pickup_addr: '',
      dropoff_addr: '',
      pickup_date: '',
      pickup_time: '',
      pax_count: 1,
      passenger_name: '',
      passenger_id_no: '',
      service_type: 'PATIENT',
      notes: '',
      responsible_person: '',
      responsible_contact: '',
      flight_code: ''
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const addTransfer = () => {
    setTransfers([...transfers, {
      pickup_addr: '',
      dropoff_addr: '',
      pickup_date: '',
      pickup_time: '',
      pax_count: 1,
      passenger_name: '',
      passenger_id_no: '',
      service_type: 'PATIENT',
      notes: '',
      responsible_person: '',
      responsible_contact: '',
      flight_code: ''
    }]);
  };

  const removeTransfer = (index) => {
    if (transfers.length > 1) {
      setTransfers(transfers.filter((_, i) => i !== index));
    }
  };

  const updateTransfer = (index, field, value) => {
    const updated = [...transfers];
    updated[index][field] = value;
    setTransfers(updated);
    
    // Hata mesajını temizle
    if (errors[`${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${index}_${field}`];
      setErrors(newErrors);
    }
  };

  const validateTransfers = () => {
    const newErrors = {};
    
    transfers.forEach((transfer, index) => {
      if (!transfer.pickup_addr.trim()) {
        newErrors[`${index}_pickup_addr`] = 'Alış adresi gereklidir';
      }
      if (!transfer.dropoff_addr.trim()) {
        newErrors[`${index}_dropoff_addr`] = 'Bırakış adresi gereklidir';
      }
      if (!transfer.pickup_date) {
        newErrors[`${index}_pickup_date`] = 'Alış tarihi gereklidir';
      }
      if (!transfer.pickup_time) {
        newErrors[`${index}_pickup_time`] = 'Alış saati gereklidir';
      }
      if (!transfer.service_type) {
        newErrors[`${index}_service_type`] = 'Hizmet tipi gereklidir';
      }
      
      // Yeni zorunlu alanlar
      if (!transfer.passenger_id_no || !transfer.passenger_id_no.trim()) {
        newErrors[`${index}_passenger_id_no`] = 'TC/Pasaport/Evrak No gereklidir';
      }
      if (!transfer.responsible_person || !transfer.responsible_person.trim()) {
        newErrors[`${index}_responsible_person`] = 'Sorumlu kişi gereklidir';
      }
      if (!transfer.responsible_contact || !transfer.responsible_contact.trim()) {
        newErrors[`${index}_responsible_contact`] = 'Sorumlu iletişim gereklidir';
      }
      
      // Havalimanı transfer için uçuş kodu zorunlu
      if (transfer.service_type === 'AIRPORT') {
        if (!transfer.flight_code || !transfer.flight_code.trim()) {
          newErrors[`${index}_flight_code`] = 'Havalimanı transferi için uçuş kodu gereklidir';
        }
      }
      
      // Tarih/saat validasyonu
      if (transfer.pickup_date && transfer.pickup_time) {
        const pickupDateTime = new Date(`${transfer.pickup_date}T${transfer.pickup_time}`);
        const now = new Date();
        if (pickupDateTime <= now) {
          newErrors[`${index}_datetime`] = 'Alış tarihi ve saati şu andan sonra olmalıdır';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateTransfers()) {
      return;
    }

    setIsSubmitting(true);
    const results = [];

    try {
      for (const transfer of transfers) {
        try {
          // Acente ID'sini user'dan al
          const transferData = {
            ...transfer,
            agency_id: user?.agency_id
          };
          
          if (!transferData.agency_id) {
            throw new Error('Acente ID bulunamadı. Lütfen tekrar giriş yapın.');
          }
          
          console.log('🚀 Transfer gönderiliyor:', {
            url: '/api/transfers/',
            data: transferData,
            user: user,
            baseURL: api.defaults.baseURL
          });
          
          const response = await api.post('/api/transfers/', transferData);
          console.log('✅ Transfer başarılı:', response.data);
          results.push({ success: true, data: response.data });
        } catch (error) {
          console.error('❌ Transfer hatası:', {
            error: error.response?.data || error.message,
            status: error.response?.status,
            url: error.config?.url
          });
          results.push({ 
            success: false, 
            error: error.response?.data?.detail || 'Transfer oluşturulamadı' 
          });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        showSuccess(`${successCount} transfer başarıyla oluşturuldu${failCount > 0 ? `, ${failCount} transfer başarısız` : ''}`);
        navigate('/agency/taleplerim');
      } else {
        showError('Hiçbir transfer oluşturulamadı');
      }
    } catch (error) {
      console.error('Submit error:', error);
      showError('Transferler gönderilirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTodayDate = () => {
    // Local timezone için doğru tarih
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const getServiceTypeLabel = (type) => {
    const types = {
      'PATIENT': 'Hasta',
      'STAFF': 'Personel', 
      'EXECUTIVE': 'Yönetici',
      'DOCUMENT': 'Evrak',
      'AIRPORT': 'Havalimanı Transfer',
      'MATERIAL': 'Malzeme'
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Transfer Talebi Oluştur</h2>
        <button
          onClick={addTransfer}
          className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Yeni Satır Ekle
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="space-y-6">
            {transfers.map((transfer, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Transfer #{index + 1}</h3>
                  {transfers.length > 1 && (
                    <button
                      onClick={() => removeTransfer(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Alış Adresi */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Alış Adresi *
                    </label>
                    <AddressAutocomplete
                      value={transfer.pickup_addr}
                      onChange={(value) => updateTransfer(index, 'pickup_addr', value)}
                      type="pickup"
                      placeholder="Alış adresi"
                      required
                      error={!!errors[`${index}_pickup_addr`]}
                    />
                    {errors[`${index}_pickup_addr`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_pickup_addr`]}</p>
                    )}
                  </div>

                  {/* Bırakış Adresi */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Bırakış Adresi *
                    </label>
                    <AddressAutocomplete
                      value={transfer.dropoff_addr}
                      onChange={(value) => updateTransfer(index, 'dropoff_addr', value)}
                      type="dropoff"
                      placeholder="Bırakış adresi"
                      required
                      error={!!errors[`${index}_dropoff_addr`]}
                    />
                    {errors[`${index}_dropoff_addr`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_dropoff_addr`]}</p>
                    )}
                  </div>

                  {/* Tarih */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Tarih *
                    </label>
                    <input
                      type="date"
                      value={transfer.pickup_date}
                      onChange={(e) => updateTransfer(index, 'pickup_date', e.target.value)}
                      min={getTodayDate()}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_pickup_date`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {errors[`${index}_pickup_date`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_pickup_date`]}</p>
                    )}
                  </div>

                  {/* Saat */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Saat *
                    </label>
                    <input
                      type="time"
                      value={transfer.pickup_time}
                      onChange={(e) => updateTransfer(index, 'pickup_time', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_pickup_time`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {errors[`${index}_pickup_time`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_pickup_time`]}</p>
                    )}
                  </div>

                  {/* Yolcu Sayısı */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Yolcu Sayısı
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={transfer.pax_count}
                      onChange={(e) => updateTransfer(index, 'pax_count', parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {/* Yolcu Adı Soyadı */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Yolcu Adı Soyadı
                    </label>
                    <input
                      type="text"
                      value={transfer.passenger_name}
                      onChange={(e) => updateTransfer(index, 'passenger_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Yolcu adı soyadı"
                    />
                  </div>

                  {/* Yolcu TC/Pasaport/Evrak No */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      TC/Pasaport/Evrak No *
                    </label>
                    <input
                      type="text"
                      value={transfer.passenger_id_no}
                      onChange={(e) => updateTransfer(index, 'passenger_id_no', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_passenger_id_no`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="TC No, Pasaport No veya Evrak No"
                    />
                    {errors[`${index}_passenger_id_no`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_passenger_id_no`]}</p>
                    )}
                  </div>

                  {/* Hizmet Tipi */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Hizmet Tipi *
                    </label>
                    <select
                      value={transfer.service_type}
                      onChange={(e) => updateTransfer(index, 'service_type', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_service_type`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                    >
                      <option value="PATIENT">Hasta</option>
                      <option value="STAFF">Personel</option>
                      <option value="EXECUTIVE">Yönetici</option>
                      <option value="DOCUMENT">Evrak</option>
                      <option value="AIRPORT">Havalimanı Transfer</option>
                      <option value="MATERIAL">Malzeme</option>
                    </select>
                    {errors[`${index}_service_type`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_service_type`]}</p>
                    )}
                  </div>

                  {/* Sorumlu Kişi */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sorumlu Kişi *
                    </label>
                    <input
                      type="text"
                      value={transfer.responsible_person}
                      onChange={(e) => updateTransfer(index, 'responsible_person', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_responsible_person`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Sorumlu kişi adı"
                    />
                    {errors[`${index}_responsible_person`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_responsible_person`]}</p>
                    )}
                  </div>

                  {/* Sorumlu İletişim */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sorumlu İletişim *
                    </label>
                    <input
                      type="text"
                      value={transfer.responsible_contact}
                      onChange={(e) => updateTransfer(index, 'responsible_contact', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_responsible_contact`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Telefon numarası"
                    />
                    {errors[`${index}_responsible_contact`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_responsible_contact`]}</p>
                    )}
                  </div>

                  {/* Uçuş Kodu */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Uçuş Kodu {transfer.service_type === 'AIRPORT' && '*'}
                    </label>
                    <input
                      type="text"
                      value={transfer.flight_code}
                      onChange={(e) => updateTransfer(index, 'flight_code', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        errors[`${index}_flight_code`] ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Örn: TK123"
                    />
                    {errors[`${index}_flight_code`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`${index}_flight_code`]}</p>
                    )}
                  </div>

                  {/* Not */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Not
                    </label>
                    <textarea
                      value={transfer.notes}
                      onChange={(e) => updateTransfer(index, 'notes', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Ek notlar..."
                    />
                  </div>
                </div>

                {errors[`${index}_datetime`] && (
                  <p className="text-red-600 text-sm mt-2">{errors[`${index}_datetime`]}</p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-3 rounded-xl hover:from-red-700 hover:to-red-800 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Gönderiliyor...' : 'Tümünü Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Acente_TalepOlustur;
